/**
 * URL ↔ form values (de)serialization for レセプト内容一覧.
 * Enables bookmarkable search conditions.
 */

import {
  REZEPT_KBN_MAIN,
  REZEPT_KBN_RELATED,
  SEARCH_RADIO_INSURER_NAME,
  SEARCH_RADIO_INSURER_TYPE,
  SORT_BY_INSURER_NUMBER,
  SORT_BY_KANA,
  SORT_BY_SERIAL,
} from './constants';
import type {
  RezeptHistoriesFormValues,
  RezeptHistoriesSearchPayload,
  RezeptKbn,
  SearchRadio,
  SortRadio,
} from './types';

const YEARMONTH_REGEX = /^\d{4}-(0[1-9]|1[0-2])$/;

const intOrNull = (v: string | null): number | null => {
  if (v == null || v === '') return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

const yearmonthOrEmpty = (v: string | null): string => {
  return v && YEARMONTH_REGEX.test(v) ? v : '';
};

const oneOfOrDefault = <T extends string>(
  v: string | null,
  allowed: readonly T[],
  fallback: T
): T => {
  return v != null && (allowed as readonly string[]).includes(v) ? (v as T) : fallback;
};

const iniOrEmpty = (v: string | null): string => {
  if (!v) return '';
  const n = Number(v);
  return Number.isInteger(n) && n >= 1 && n <= 10 ? String(n) : '';
};

/** Read initial page number from URL (defaults to 1). */
export function searchParamsToPage(sp: URLSearchParams): number {
  const n = intOrNull(sp.get('page'));
  return n && n >= 1 ? n : 1;
}

/**
 * Deserialize URL params → form values.
 * Returns null if URL has no query params (initial bare page load).
 */
export function searchParamsToFormValues(
  sp: URLSearchParams,
  defaults: RezeptHistoriesFormValues
): RezeptHistoriesFormValues | null {
  if ([...sp.keys()].length === 0) return null;

  return {
    search_yearmonth: yearmonthOrEmpty(sp.get('search_yearmonth')) || defaults.search_yearmonth,
    search_visitmonth: yearmonthOrEmpty(sp.get('search_visitmonth')),
    massager_id: intOrNull(sp.get('massager_id')),
    search_radio: oneOfOrDefault<SearchRadio>(
      sp.get('search_radio'),
      [SEARCH_RADIO_INSURER_NAME, SEARCH_RADIO_INSURER_TYPE] as const,
      defaults.search_radio
    ),
    insurer_type_id: intOrNull(sp.get('insurer_type_id')),
    ini: iniOrEmpty(sp.get('ini')),
    insurer_name: sp.get('insurer_name') ?? '',
    rezept_kbn: oneOfOrDefault<RezeptKbn>(
      sp.get('rezept_kbn'),
      [REZEPT_KBN_MAIN, REZEPT_KBN_RELATED] as const,
      defaults.rezept_kbn
    ),
    sort_radio: oneOfOrDefault<SortRadio>(
      sp.get('sort_radio'),
      [SORT_BY_SERIAL, SORT_BY_KANA, SORT_BY_INSURER_NUMBER] as const,
      defaults.sort_radio
    ),
  };
}

/**
 * Serialize form values (+ optional page) → URLSearchParams.
 * Empty / null / default-like values are omitted to keep URLs short.
 */
export function formValuesToSearchParams(
  values: RezeptHistoriesFormValues,
  page?: number
): URLSearchParams {
  const out = new URLSearchParams();
  if (values.search_yearmonth) out.set('search_yearmonth', values.search_yearmonth);
  if (values.search_visitmonth) out.set('search_visitmonth', values.search_visitmonth);
  if (values.massager_id != null) out.set('massager_id', String(values.massager_id));
  if (values.search_radio) out.set('search_radio', values.search_radio);
  if (values.insurer_type_id != null) out.set('insurer_type_id', String(values.insurer_type_id));
  if (values.ini) out.set('ini', values.ini);
  if (values.insurer_name) out.set('insurer_name', values.insurer_name);
  if (values.rezept_kbn) out.set('rezept_kbn', values.rezept_kbn);
  if (values.sort_radio) out.set('sort_radio', values.sort_radio);
  if (page && page >= 1) out.set('page', String(page));
  return out;
}

/** Form values → API payload (strips empty strings / nulls). */
export function formValuesToSearchPayload(
  values: RezeptHistoriesFormValues,
  page: number,
  perPage: number
): RezeptHistoriesSearchPayload {
  return {
    search_yearmonth: values.search_yearmonth,
    search_visitmonth: values.search_visitmonth || undefined,
    massager_id: values.massager_id ?? undefined,
    search_radio: values.search_radio,
    insurer_type_id: values.insurer_type_id ?? undefined,
    ini: values.ini || undefined,
    insurer_name: values.insurer_name || undefined,
    rezept_kbn: values.rezept_kbn,
    sort_radio: values.sort_radio,
    page,
    per_page: perPage,
  };
}
